import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { MarketingSEO } from '@/components/seo/MarketingSEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { smartAlertPageMap, smartAlertPages } from '@/data/smartAlertPages';
import { getKeywordsForPath } from '@/data/seoKeywordMap';
import { Link } from 'react-router-dom';

const monitoringSignals = [
  'Unknown or unsaved numbers that drive most of the conversation',
  'Contacts that cycle through multiple numbers, usernames, or email handles',
  'Sudden bursts of messages from someone your child rarely talks to',
  'Numbers that reappear after being blocked or ignored',
  'Outreach patterns that look more like chasing than a normal friendship'
];

const alertScreenshotExamples = [
  {
    title: "Example: unknown number messaging late at night",
    description:
      "This alert flags when a brand-new number suddenly sends a rush of late-night messages that don't match your child's usual patterns.",
  },
  {
    title: "Example: persistent contact switching numbers",
    description:
      "See when someone hops between multiple numbers or usernames to get around being ignored or blocked.",
  },
  {
    title: "Example: reappearing contact after block",
    description:
      "Joey connects the dots when a previously blocked person comes back with new contact info.",
  },
];

const realWorldScenarios = [
  {
    title: "The \"friendly\" older teen",
    description:
      "Your 12-year-old joins a group chat for a game. An older teen from the group starts messaging them directly late at night, then tries a new number when they stop replying. Joey flags the churn in phone numbers and persistent outreach so you can step in.",
  },
  {
    title: 'The unknown "friend of a friend"',
    description:
      "A new number appears, saying they got your child's contact from \"someone in class\", but your child is not sure who. They keep nudging to move to another app and keep the chat secret. Suspicious Contacts highlights this as unusual outreach from an unknown contact.",
  },
  {
    title: "The returning blocked number",
    description:
      "Months after your child blocked someone, that person reappears with a new number and tries again. Joey notices the pattern and surfaces an alert so you can decide whether to re-block, report, or escalate.",
  },
];

const smartAlertCategoryLinks = smartAlertPages
  .filter((category) => category.slug !== 'suspicious-contacts')
  .map(({ slug, path, title, cardDescription }) => ({
    slug,
    path,
    title,
    description: cardDescription ?? ''
  }));

const SmartAlertSuspiciousContacts = () => {
  const page = smartAlertPageMap['suspicious-contacts'];

  if (!page) {
    throw new Error('Smart alert data missing for suspicious contacts');
  }

  const keywords = getKeywordsForPath(page.path);
  const canonicalUrl = `https://www.joeyfamily.com${page.path}`;

  return (
    <>
      <MarketingSEO
        title={`${page.title} Smart Alert | Joey`}
        description={page.seoDescription}
        url={canonicalUrl}
        keywords={keywords}
      />

      <Navigation />

      <main className="bg-gradient-to-b from-white via-slate-50 to-white">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_rgba(38,132,255,0.3),_transparent_55%)]" />
          <div className="relative max-w-6xl mx-auto px-4 pt-36 pb-24 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr),380px] items-start">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${page.accentColor}`}>
                    <img src={page.icon.src} alt={page.icon.alt} className="w-9 h-9" loading="lazy" />
                  </div>
                  <span className={`text-sm font-semibold uppercase tracking-[0.3em] ${page.accentText}`}>
                    Smart alert category
                  </span>
                </div>
                <div className="space-y-4">
                  <h1 className="text-4xl md:text-5xl font-semibold text-brand-navy">Suspicious contacts</h1>
                  <p className="text-2xl text-slate-800">
                    Spot strangers and risky numbers before conversations get too close.
                  </p>
                </div>
                <p className="text-slate-700 leading-relaxed mt-6 text-lg max-w-2xl">
                  Joey keeps an eye on unfamiliar numbers, sudden spikes in outreach, or contacts that keep coming back after being
                  blocked. Instead of scrolling through every message, you get a clear heads-up when someone new starts orbiting
                  your child. The goal is simple: help you notice early, ask calm questions, and keep your child in control of who
                  can reach them.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg">
                    <Link to="/get-started">Get Joey Smart Alerts</Link>
                  </Button>
                  <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Signals, not surveillance</p>
                </div>
              </div>

              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-brand-navy mb-5">What this alert monitors</h2>
                  <ul className="space-y-4 text-slate-700">
                    {monitoringSignals.map((signal) => (
                      <li key={signal} className="flex gap-3">
                        <span className="mt-2 h-2.5 w-2.5 rounded-full bg-brand-cyan" />
                        <span>{signal}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                    Signals, not full chat logs. Joey focuses on patterns so you can step in without reading every message.
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why this alert matters */}
        <section className="py-20 px-4 bg-white border-t border-slate-100">
          <div className="max-w-5xl mx-auto grid gap-12 lg:grid-cols-[minmax(0,1.2fr),minmax(0,0.8fr)] items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Why this alert matters</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-2">Why suspicious contacts matter</h2>
              <div className="space-y-5 text-slate-700 leading-relaxed text-lg mt-8">
                <p>
                  New names and numbers are a normal part of growing up. Group chats, games, and school friends often blend together.
                  The worry for many parents is not every new contact. It is the one older teen, stranger, or pushy new "friend" who
                  quietly moves the conversation into a private space.
                </p>
                <p>
                  Suspicious Contacts Alerts are built for that grey area. They help you see when someone is putting in a lot of effort to
                  reach your child, switching numbers, or pushing for one-on-one contact. Instead of guessing, you get a gentle signal that
                  says: "This person deserves a second look."
                </p>
              </div>
            </div>
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-6 space-y-5">
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-500">Parent takeaways</p>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-yellow" />
                    <span>Clarity on who's putting pressure on your child and how often.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-navy" />
                    <span>Context that helps you start calm, non-accusatory conversations.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-teal" />
                    <span>A way to act before risky contacts escalate or move platforms.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What the alert looks like */}
        <section className="py-20 px-4 bg-slate-900 text-white">
          <div className="max-w-5xl mx-auto space-y-10">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-white/60">In the Joey dashboard</p>
              <h2 className="text-3xl md:text-4xl font-semibold mt-3">What this alert looks like in Joey</h2>
              <p className="text-lg text-white/90 mt-4">
                When Joey notices risky patterns, you'll see a Smart Alert in your parent dashboard. A typical card might say:
                "New number is driving most of the conversation with [Child's name]. Patterns look unusual for their normal
                contacts. Check who this is and how they met."
              </p>
              <div className="mt-6 space-y-3 text-white/80">
                <p>Each alert includes:</p>
                <ul className="space-y-2">
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-cyan" />
                    <span>The contact label or phone number</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-cyan" />
                    <span>A short description of the pattern Joey spotted</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-cyan" />
                    <span>The timeframe, such as "in the last 7 days"</span>
                  </li>
                </ul>
                <p>
                  You can open the alert to see more context when you need it, or simply use it as a prompt to check in with your
                  child.
                </p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {alertScreenshotExamples.map((example) => (
                <Card key={example.title} className="bg-slate-800/70 border border-white/10">
                  <CardContent className="p-5 space-y-4">
                    <div className="h-48 rounded-xl border border-dashed border-white/30 bg-slate-900/50 flex items-center justify-center text-white/50 text-sm text-center px-4">
                      Screenshot placeholder
                    </div>
                    <div>
                      <p className="font-semibold">{example.title}</p>
                      <p className="text-sm text-white/80 mt-2 leading-relaxed">{example.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Real world scenarios */}
        <section className="py-20 px-4 bg-white border-t border-slate-100">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Real-world examples</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-2">What this might look like in real life</h2>
              <p className="text-lg text-slate-600 mt-4">
                Each scenario is different, but the goal is the same: you know who is trying to reach your child before things go too far.
              </p>
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              {realWorldScenarios.map((scenario) => (
                <Card key={scenario.title} className="h-full border-slate-200 shadow-sm">
                  <CardContent className="p-6 flex flex-col">
                    <span className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-400">Scenario</span>
                    <h3 className="text-xl font-semibold text-brand-navy mt-3">{scenario.title}</h3>
                    <p className="text-slate-700 leading-relaxed mt-4">{scenario.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* See all categories */}
        <section className="py-20 px-4 bg-slate-50 border-t border-slate-100">
          <div className="max-w-6xl mx-auto space-y-10">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl space-y-4">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">See all smart alert categories</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-brand-navy">See all Smart Alert categories</h2>
                <p className="text-slate-700 text-lg">
                  Each Smart Alert focuses on a different type of risk so you can respond with confidence, not guesswork.
                </p>
              </div>
              <Button asChild size="lg" className="shrink-0">
                <Link to="/features/smart-alerts">Return to Smart Alerts</Link>
              </Button>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              {smartAlertCategoryLinks.map((category) => (
                <Card key={category.slug} className="border-slate-200 bg-white hover:border-brand-cyan transition">
                  <CardContent className="p-6 flex flex-col gap-4">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-400">Smart alert</p>
                      <h3 className="text-2xl font-semibold text-brand-navy mt-2">{category.title}</h3>
                    </div>
                    <p className="text-slate-600 flex-1">{category.description}</p>
                    <Button asChild variant="ghost" className="justify-start px-0 text-brand-navy hover:text-brand-cyan">
                      <Link to={category.path} aria-label={`Read about the ${category.title} Smart Alert`}>
                        Explore alert
                        <span className="ml-2" aria-hidden>
                          -&gt;
                        </span>
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SmartAlertSuspiciousContacts;

